import { logger } from '@/lib/logger';
import { getDynamicUrl } from '@/lib/network-utils';

/**
 * Environment configuration helper
 * Provides centralized access to environment variables and feature flags
 */

const requiredEnvironmentValues = {
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
  VITE_CLERK_PUBLISHABLE_KEY: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
};

export const environment = {
  // Environment type detection
  isDevelopment: import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.DEV,
  isStaging: import.meta.env.VITE_ENVIRONMENT === 'staging',
  isProduction: import.meta.env.VITE_ENVIRONMENT === 'production' || import.meta.env.PROD,

  // Supabase configuration with dynamic URL handling for network access
  supabase: {
    url: getDynamicUrl(import.meta.env.VITE_SUPABASE_URL),
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    edgeFunctionUrl: getDynamicUrl(import.meta.env.VITE_EDGE_FUNCTION_URL),
  },

  // Clerk authentication
  clerk: {
    publishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
  },

  // Application URLs with dynamic handling for network access
  app: {
    url:
      getDynamicUrl(import.meta.env.VITE_PUBLIC_APP_URL) ||
      (typeof window !== 'undefined' ? window.location.origin : ''),
  },

  // Feature flags based on environment
  features: {
    // Enable debug mode in development and staging
    enableDebugMode: import.meta.env.VITE_ENVIRONMENT !== 'production',

    // Enable analytics only in production
    enableAnalytics: import.meta.env.VITE_ENVIRONMENT === 'production',

    // Enable error reporting in staging and production
    enableErrorReporting: import.meta.env.VITE_ENVIRONMENT !== 'development',

    // Enable experimental features in development
    enableExperimentalFeatures: import.meta.env.VITE_ENVIRONMENT === 'development',
  },

  // Helper function to get current environment name
  getCurrentEnvironment(): string {
    if (this.isDevelopment) return 'development';
    if (this.isStaging) return 'staging';
    if (this.isProduction) return 'production';
    return 'unknown';
  },

  // Validate required environment variables
  validate(): void {
    const missing = Object.entries(requiredEnvironmentValues)
      .filter(([, value]) => !value)
      .map(([key]) => key);

    if (missing.length > 0) {
      logger.error('Missing required environment variables:', missing);
      throw new Error(
        `Missing required environment variables: ${missing.join(', ')}. Please check your .env file.`,
      );
    }
  },
};

// Validate on initialization (only in development)
if (environment.isDevelopment) {
  try {
    environment.validate();
  } catch (error) {
    logger.error('Environment validation failed:', error);
  }
}
